import React from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import Modal from '../../../components/modal/PropdocsModal';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { getDealsForTeam } from '../../../api/queries';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { InlineTextField, TextField, SearchableSelectField } from '../../../components/fields';
import SubmitButton from '../../../components/submit-button';
import { RadioField } from '../../../components/checkbox-or-radio-field';
import { useMutation, useQuery } from '@apollo/client';
import { createExpressDocumentFromTemplate } from '../../../api/mutations';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { TitleFormattedMessage } from '../../../components/titleFormattedMessage';
import styles from './styles.module.scss';

interface CreateExpressTemplateDocumentProps {
  isOpen: boolean;
  onClose: () => void;
  templateId: string;
  templateName: string;
  teamId: string;
  // The template has edits that aren't published; documents are created from
  // the published version, so say so before the user commits.
  usesLastPublishedVersion?: boolean;
  lastPublishedAt?: string | null;
}

const schema = yup
  .object({
    existingDealId: yup.string().nullable(),
    newDealName: yup.string().nullable(),
    assignDeal: yup.string(),
    documentName: yup.string().required('documentName is required'),
  })
  .test('either-or', 'You must select an existing deal or add a title for a new deal', (obj: any) => {
    const { dealId, newDealName, documentName } = obj;
    // Check that exactly one of the fields is filled
    return documentName === '' && ((dealId && !newDealName) || (!dealId && newDealName));
  });

enum FormikValuesEnum {
  assignExistingDeal = 'existing',
  assignNewDeal = 'new',
}

const CreateExpressTemplateDocument = ({
  isOpen,
  onClose,
  templateName,
  teamId,
  templateId,
  usesLastPublishedVersion = false,
  lastPublishedAt = null,
}: CreateExpressTemplateDocumentProps) => {
  const navigate = useNavigate();

  const {
    loading: dealsDataLoading,
    data: dealsData,
    error: graphqlError,
  } = useQuery(getDealsForTeam, {
    variables: {
      teamId,
    },
  });

  const [triggerCreateExpressDocument] = useMutation(createExpressDocumentFromTemplate, {
    refetchQueries: [
      {
        query: getDealsForTeam,
        variables: {
          teamId,
        },
      },
    ],
  });

  const onSubmit = (values: any) => {
    const variables =
      values.assignDeal === FormikValuesEnum.assignNewDeal
        ? {
            teamId,
            dealTitle: values.newDealName,
            templateId,
            documentTitle: values.documentName,
          }
        : {
            teamId,
            dealId: values.existingDealId,
            templateId,
            documentTitle: values.documentName,
          };
    triggerCreateExpressDocument({
      variables,
    }).then(({ data }) => {
      if (data) {
        const routeRedirect = `teams/${teamId}/deals/${data.createExpressDocumentFromTemplate?.deal.id}/documents/${data.createExpressDocumentFromTemplate?.id}`;
        navigate(`/${routeRedirect}`);
      }
    });
  };

  const validateSelectedField = (value: any) => !value?.trim();

  const getSelectDealDropdownOptions = () =>
    dealsData.dealsForTeam
      .slice() // Create a copy to avoid mutating original
      .sort((a: any, b: any) => a.title.localeCompare(b.title)) // Sort alphabetically by title
      .map((deal: any) => ({
        label: deal.title, // Use simple string for SearchableSelectField compatibility
        value: deal.id,
      }));

  return (
    <Modal
      show={isOpen}
      setShow={onClose}
      size="md"
      title={<FormattedMessage id="title.first-lease-template" defaultMessage="Create a Document from Template" />}
    >
      <>
        <div>
          <p className="mb-2">
            <b>Template name: </b>
            {templateName}
          </p>
          {usesLastPublishedVersion && (
            <p className="text-muted small mb-2" data-testid="use-template-published-version-note">
              {lastPublishedAt ? (
                <FormattedMessage
                  id="createExpressTemplateDocument.uses-published-version-dated"
                  defaultMessage="Uses the last published version ({date}). Recent edits aren't included until they're published."
                  values={{ date: <FormattedDate value={lastPublishedAt} year="numeric" month="long" day="numeric" /> }}
                />
              ) : (
                <FormattedMessage
                  id="createExpressTemplateDocument.uses-published-version"
                  defaultMessage="Uses the last published version. Recent edits aren't included until they're published."
                />
              )}
            </p>
          )}
          <Formik
            validationSchema={schema}
            onSubmit={onSubmit}
            isInitialValid={false}
            initialValues={{
              assignDeal: FormikValuesEnum.assignExistingDeal,
              existingDealId: null,
              newDealName: null,
              documentName: '',
            }}
          >
            {({ errors, isSubmitting, isValid, setFieldValue, values }) => {
              const isExistingDealSelected = values.assignDeal === FormikValuesEnum.assignExistingDeal;

              return (
                <Form noValidate>
                  <div>
                    <Row noGutters className="justify-content-center flex-nowrap">
                      <Col>
                        <div style={{ marginBottom: '-0.5rem' }}>
                          <TextField
                            id="createExpressTemplateDocument.documentName"
                            name="documentName"
                            type="documentName"
                            label={
                              <FormattedMessage
                                id="createExpressTemplateDocument.documentName"
                                defaultMessage="Document Name"
                              />
                            }
                          />
                        </div>
                      </Col>
                    </Row>
                    <Row noGutters className="justify-content-center flex-nowrap">
                      <Col>
                        <p className="text-muted small mt-1 mb-0" style={{ paddingBottom: '1rem' }}>
                          Choose an existing deal or create a new deal to add your document.
                        </p>
                      </Col>
                    </Row>
                  </div>
                  <div className={styles.dealSelectionContainer}>
                    <Row className="align-items-center mb-2 w-100">
                      <Col xs={4}>
                        <RadioField
                          id="createExpressDocument.team.existing"
                          name="assignDeal"
                          value="existing"
                          label={
                            <FormattedMessage
                              id="createExpressDocument.team.existing.label"
                              defaultMessage="Existing Deal"
                            />
                          }
                          hidefeedback="true"
                          wrapperClassName="mb-0"
                          onClick={async () => {
                            await setFieldValue('assignDeal', FormikValuesEnum.assignExistingDeal);
                            await setFieldValue('newDealName', null);
                          }}
                        />
                      </Col>
                      <Col xs={8}>
                        <SearchableSelectField
                          // @ts-ignore
                          id="createExpressDocument.deal.existing"
                          name="existingDealId"
                          options={getSelectDealDropdownOptions()}
                          isDisabled={!isExistingDealSelected}
                          placeholder="Select a deal"
                          validate={isExistingDealSelected && validateSelectedField}
                          isClearable={false}
                          isSearchable={true}
                          maxMenuHeight={120}
                          menuPlacement="auto"
                        />
                      </Col>
                    </Row>
                    <Row className="align-items-center mb-2 w-100">
                      <Col xs={4}>
                        <RadioField
                          id="createExpressDocument.deal.new"
                          name="assignDeal"
                          value="new"
                          label={
                            <FormattedMessage id="createExpressDocument.team.new.label" defaultMessage="New Deal" />
                          }
                          hidefeedback="true"
                          wrapperClassName="mb-0"
                          onClick={async () => {
                            await setFieldValue('assignDeal', FormikValuesEnum.assignNewDeal);
                            await setFieldValue('existingDealId', null);
                          }}
                        />
                      </Col>
                      <Col xs={8}>
                        <InlineTextField
                          // @ts-ignore
                          id="createExpressDocument.newDealName"
                          data-testid="createExpressDocument.newDealName"
                          name="newDealName"
                          disabled={isExistingDealSelected}
                          validate={!isExistingDealSelected && validateSelectedField}
                          // className={isExistingDealSelected && styles.noValidate}
                        />
                      </Col>
                    </Row>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1.5rem' }}>
                    <Button variant="link-secondary" onClick={onClose} data-testid="cancel-change-password">
                      <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
                    </Button>
                    <SubmitButton
                      label={<FormattedMessage id="cta.create-document" defaultMessage="Create Document" />}
                      disabled={isSubmitting || !isValid}
                      isSubmitting={isSubmitting}
                    />
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </>
    </Modal>
  );
};

export default CreateExpressTemplateDocument;
